/**
 * App Settings Service
 * Firebase-based storage for application-wide settings
 */
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

const SETTINGS_COLLECTION = 'systemSettings';
const APP_SETTINGS_DOC = 'appSettings';

/**
 * Get default app settings
 */
const getDefaultSettings = () => ({
  feedbackUrl: '',
  feedbackButtonText: 'Submit Feedback'
});

// Cache for app settings
let settingsCache = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get app settings from Firestore
 * @param {boolean} useCache - Whether to use cached data
 * @returns {Promise<Object>} App settings object
 */
export const getAppSettings = async (useCache = true) => {
  try {
    const now = Date.now();

    // Check cache first
    if (useCache && settingsCache && (now - cacheTimestamp) < CACHE_DURATION) {
      return settingsCache;
    }

    const settingsRef = doc(db, SETTINGS_COLLECTION, APP_SETTINGS_DOC);
    const settingsDoc = await getDoc(settingsRef);

    if (settingsDoc.exists()) {
      const settings = {
        ...getDefaultSettings(),
        ...settingsDoc.data()
      };
      settingsCache = settings;
      cacheTimestamp = now;
      return settings;
    }

    // Return defaults if no settings exist yet
    return getDefaultSettings();
  } catch (error) {
    console.error('Error getting app settings:', error);
    // Return defaults on error
    return getDefaultSettings();
  }
};

/**
 * Save app settings to Firestore
 * @param {Object} settings - App settings object
 * @param {string} updatedBy - Email of user saving settings
 * @returns {Promise<void>}
 */
export const saveAppSettings = async (settings, updatedBy) => {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, APP_SETTINGS_DOC);

    const settingsToSave = {
      ...settings,
      lastUpdated: serverTimestamp(),
      updatedBy: updatedBy || 'unknown'
    };

    await setDoc(settingsRef, settingsToSave, { merge: true });

    // Clear cache after save
    settingsCache = null;
    cacheTimestamp = 0;

    console.log('App settings saved successfully');
  } catch (error) {
    console.error('Error saving app settings:', error);
    throw new Error('Failed to save app settings. Please try again.');
  }
};

/**
 * Clear the settings cache
 */
export const clearAppSettingsCache = () => {
  settingsCache = null;
  cacheTimestamp = 0;
};
